import React, {
  createContext,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

type FormState = Record<string, any>;

type ChangeValue = (valueOrUpdater: any) => void;

type FieldRenderProps = {
  value: any;
  changeValue: ChangeValue;
};

type FormContextValue = {
  state: FormState;
  setFieldValue: (id: string, valueOrUpdater: any) => void;
};

const FormContext = createContext<FormContextValue | null>(null);

export type FormHandle = {
  getValue: (id: string) => any;
  changeValue: (id: string, valueOrUpdater: any) => void;
};

type FormProps = {
  initialState?: FormState;
  children: React.ReactNode;
};

export const Form = forwardRef<FormHandle, FormProps>(({initialState = {}, children}, ref) => {
  const [state, setState] = useState<FormState>(initialState);
  const stateRef = useRef<FormState>(initialState);

  const setFieldValue = (id: string, valueOrUpdater: any) => {
    setState((prev) => {
      const prevValue = prev[id];
      const nextValue =
        typeof valueOrUpdater === 'function' ? valueOrUpdater(prevValue) : valueOrUpdater;
      const nextState = {...prev, [id]: nextValue};
      stateRef.current = nextState;
      return nextState;
    });
  };

  useImperativeHandle(
    ref,
    () => ({
      getValue: (id: string) => stateRef.current[id],
      changeValue: (id: string, valueOrUpdater: any) => setFieldValue(id, valueOrUpdater),
    }),
    [],
  );

  const ctx = useMemo<FormContextValue>(
    () => ({
      state,
      setFieldValue,
    }),
    [state],
  );

  return <FormContext.Provider value={ctx}>{children}</FormContext.Provider>;
});

type FieldProps = {
  id: string;
  children: (props: FieldRenderProps) => React.ReactNode;
};

export const Field: React.FC<FieldProps> = ({id, children}) => {
  const ctx = React.useContext(FormContext);

  if (!ctx) {
    throw new Error('Field must be used within a Form');
  }

  const value = ctx.state[id];
  const changeValue: ChangeValue = (valueOrUpdater) => ctx.setFieldValue(id, valueOrUpdater);

  return <>{children({value, changeValue})}</>;
};
